const trackingQueryKey = /^(?:utm_.*|fbclid|gclid|dclid|msclkid|igshid|mc_cid|mc_eid|ref_src|ref_url|si|feature)$/i;
const timestampDigits = '(?:\\d{1,2}:\\d{2}:\\d{2}|\\d{1,3}:\\d{2})';
const chapterLinePattern = new RegExp(`^[ \\t]*(\\([ \\t]*${timestampDigits}[ \\t]*\\)|\\[[ \\t]*${timestampDigits}[ \\t]*\\]|${timestampDigits})[ \\t]+(\\S.*)$`);

const mergeText = (segments, text) => {
    if (!text) return;
    const previous = segments[segments.length - 1];
    if (previous?.type === 'text') previous.text += text;
    else segments.push({ type: 'text', text });
};

export const normalizeDescriptionWhitespace = text => text
    .replace(/\r\n?/g, '\n')
    .replace(/[\u00a0\u2002\u2003\u2009]/g, ' ')
    .replace(/[\u200b\u2060]/g, '')
    .replace(/[^\S\n]*\n[^\S\n]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const parsedHttpUrl = value => {
    if (!/^https?:\/\//i.test(value)) return null;
    try {
        const parsed = new URL(value);
        return parsed.hostname && !parsed.username && !parsed.password ? parsed : null;
    } catch {
        return null;
    }
};

const displayHost = (parsed, url) => {
    const authority = url.replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
    const port = authority.match(/:(\d+)$/)?.[1] || parsed.port;
    return parsed.hostname.toLowerCase().replace(/^www\./, '') + (port ? `:${port}` : '');
};

export const readableDescriptionUrl = url => {
    const parsed = parsedHttpUrl(url);
    if (!parsed) return url;
    const host = displayHost(parsed, url);
    // Keep publisher escaping and query order rather than serializing URLSearchParams.
    const suffix = url.replace(/^https?:\/\/[^/?#]+/i, '');
    const fragmentIndex = suffix.indexOf('#');
    const fragment = fragmentIndex >= 0 ? suffix.slice(fragmentIndex) : '';
    const beforeFragment = fragmentIndex >= 0 ? suffix.slice(0, fragmentIndex) : suffix;
    const queryIndex = beforeFragment.indexOf('?');
    const path = (queryIndex >= 0 ? beforeFragment.slice(0, queryIndex) : beforeFragment).replace(/\/+$/, '');
    const queryFields = queryIndex >= 0 ? beforeFragment.slice(queryIndex + 1).split('&').filter(field => {
        let key = field.split('=')[0];
        try { key = decodeURIComponent(key.replace(/\+/g, ' ')); } catch { /* Keep malformed escaping readable. */ }
        return !trackingQueryKey.test(key);
    }) : [];
    const readableSuffix = path + (queryFields.length ? `?${queryFields.join('&')}` : '') + fragment;
    const characters = Array.from(readableSuffix);
    const budget = Math.max(16, 64 - Array.from(host).length);
    if (Array.from(host).length + characters.length <= 64) return host + readableSuffix;
    if (characters.length <= budget) return host + readableSuffix;
    const leading = Math.floor((budget - 1) / 2);
    return host + characters.slice(0, leading).join('') + '…' + characters.slice(-(budget - 1 - leading)).join('');
};

const sourceLink = (url, label = '') => {
    const parsed = parsedHttpUrl(url);
    if (!parsed) return null;
    const cleanLabel = normalizeDescriptionWhitespace(label).replace(/\s+/g, ' ');
    const urlShapedLabel = /^(?:https?:\/\/|www\.)/i.test(cleanLabel) ||
        /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/?#:]|$)/i.test(cleanLabel);
    return {
        type: 'link', url,
        display: cleanLabel && !urlShapedLabel && cleanLabel !== url
            ? `${cleanLabel} · ${displayHost(parsed, url)}` : readableDescriptionUrl(url),
    };
};

// Template contents stay inert, including image/frame resources, and are never attached.
const flattenMarkup = (source, storeLink, reserveMarker) => {
    const template = document.createElement('template');
    // Angle autolinks are text URLs, not unknown HTML elements.
    template.innerHTML = source.replace(/<(https?:\/\/[^\s<>]+)>/gi, '$1');
    reserveMarker(template.content.textContent);
    const walk = node => {
        if (node.nodeType === 3) return node.nodeValue;
        if (node.nodeType !== 1) return '';
        const name = node.tagName.toLowerCase();
        if (['script', 'style', 'template', 'img', 'iframe', 'object', 'embed', 'link', 'meta', 'source', 'audio', 'video', 'picture'].includes(name)) return '';
        if (name === 'br') return '\n';
        const contents = Array.from(node.childNodes).map(walk).join('');
        if (name === 'a') {
            const link = sourceLink(node.getAttribute('href') || '', contents);
            return link ? storeLink(link) : storeLink({ type: 'text', text: contents });
        }
        if (name === 'li') return `• ${contents}\n`;
        if (name === 'p') return `${contents}\n\n`;
        if (/^(?:div|h[1-6]|ul|ol|tr|blockquote|section|article)$/.test(name)) return `${contents}\n`;
        if (/^(?:td|th)$/.test(name)) return `${contents} `;
        return contents;
    };
    return Array.from(template.content.childNodes).map(walk).join('');
};

const flattenMarkdownLinks = (text, storeLink) => {
    const pattern = /\[([^\]\n]*)\]\(/g;
    let result = '', lastIndex = 0, match;
    while ((match = pattern.exec(text))) {
        let index = pattern.lastIndex, depth = 1;
        const start = index;
        while (index < text.length && depth > 0 && !/\s/.test(text[index])) {
            if (text[index] === '(') depth += 1;
            if (text[index] === ')') depth -= 1;
            if (depth > 0) index += 1;
        }
        if (depth !== 0) continue;
        const link = sourceLink(text.slice(start, index), match[1]);
        result += text.slice(lastIndex, match.index) + storeLink(link || { type: 'text', text: match[1] });
        lastIndex = index + 1;
        pattern.lastIndex = lastIndex;
    }
    return result + text.slice(lastIndex);
};

const trimUrlPunctuation = candidate => {
    let value = candidate;
    const closing = { ')': '(', ']': '[', '}': '{' };
    while (value) {
        const last = value[value.length - 1];
        if (/[.,;:!?]/.test(last)) value = value.slice(0, -1);
        else if (closing[last] && value.split(last).length > value.split(closing[last]).length) value = value.slice(0, -1);
        else break;
    }
    return value;
};

const timestampSeconds = digits => {
    const components = digits.split(':').map(Number);
    if (components[components.length - 1] >= 60 || (components.length === 3 && components[1] >= 60)) return null;
    return components.length === 3
        ? components[0] * 3600 + components[1] * 60 + components[2]
        : components[0] * 60 + components[1];
};

// Inspect ordered chapter lists before links split their titles into tokens.
// Repair missing blank lines only in lists that already use paragraph spacing.
// Ordered context also distinguishes titles beginning with AM/PM from clock times.
const chapterLayout = text => {
    const lines = text.split('\n');
    const meridiemLines = new Map();
    const blankBefore = new Set();
    let run = [];
    const finishRun = () => {
        if (run.length >= 2 && run.some(entry => !entry.meridiemTitle) &&
            run.slice(1).every((entry, index) => run[index].seconds < entry.seconds)) {
            run.filter(entry => entry.meridiemTitle).forEach(entry => meridiemLines.set(entry.line, entry.markerOffset));
            if (run.slice(1).some((entry, index) => entry.line > run[index].line + 1)) {
                run.slice(1).forEach((entry, index) => {
                    if (entry.line === run[index].line + 1) blankBefore.add(entry.line);
                });
            }
        }
        run = [];
    };
    for (const [index, line] of lines.entries()) {
        if (!line.trim()) continue;
        const match = chapterLinePattern.exec(line);
        if (!match) {
            finishRun();
            continue;
        }
        const marker = match[1];
        const digits = marker[0] === '(' || marker[0] === '[' ? marker.slice(1, -1).trim() : marker;
        const seconds = timestampSeconds(digits);
        const title = match[2];
        const meridiemTitle = /^[ap]m\b/i.test(title);
        if (seconds === null || (meridiemTitle && !title.slice(2).trim())) {
            finishRun();
            continue;
        }
        run.push({ line: index, markerOffset: line.indexOf(marker), seconds, meridiemTitle });
    }
    finishRun();

    const normalized = [];
    const meridiemStarts = new Set();
    let offset = 0;
    lines.forEach((line, index) => {
        if (blankBefore.has(index)) {
            normalized.push('');
            offset += 1;
        }
        if (meridiemLines.has(index)) meridiemStarts.add(offset + meridiemLines.get(index));
        normalized.push(line);
        offset += line.length + 1;
    });
    return { text: normalized.join('\n'), meridiemStarts };
};

const appendTimestamps = (segments, text, sourceOffset, chapterStarts) => {
    const pattern = new RegExp(timestampDigits, 'g');
    let lastIndex = 0, match;
    while ((match = pattern.exec(text))) {
        const start = match.index, end = pattern.lastIndex;
        if (/[\p{L}\p{N}_:$]/u.test(text[start - 1] || '') || /[\p{L}\p{N}_:]/u.test(text[end] || '')) continue;
        const seconds = timestampSeconds(match[0]);
        if (seconds === null) continue;
        let tokenStart = start, tokenEnd = end;
        let before = start - 1, after = end;
        while (before >= lastIndex && /\s/.test(text[before])) before -= 1;
        while (after < text.length && /\s/.test(text[after])) after += 1;
        if ((text[before] === '(' && text[after] === ')') || (text[before] === '[' && text[after] === ']')) {
            tokenStart = before;
            tokenEnd = after + 1;
        }
        const clockFollows = /^[ \t]*[ap]m\b/i.test(text.slice(tokenEnd)) && !chapterStarts.has(sourceOffset + tokenStart);
        if (clockFollows || /\$\s*$/.test(text.slice(0, tokenStart))) continue;
        mergeText(segments, text.slice(lastIndex, tokenStart));
        segments.push({ type: 'timestamp', display: match[0], seconds });
        lastIndex = tokenEnd;
        pattern.lastIndex = tokenEnd;
    }
    mergeText(segments, text.slice(lastIndex));
};

const appendPlainText = (segments, text, sourceOffset, chapterStarts) => {
    // Consume unsupported schemes and emails whole so their domains/times stay plain.
    const pattern = /(?<http>https?:\/\/[^\s<>"']+)|(?<email>[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9.-]+\.[a-z]{2,})|(?<bare>(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?![a-z0-9-])(?::\d{1,5})?(?:[/?#][^\s<>"']*)?)|(?<unsupported>[a-z][a-z0-9+.-]*:[^\s<>"']+)/gi;
    let lastIndex = 0, match;
    while ((match = pattern.exec(text))) {
        const candidate = match[0];
        const prefix = text[match.index - 1] || '';
        if (/[\p{L}\p{N}_@./-]/u.test(prefix)) continue;
        appendTimestamps(segments, text.slice(lastIndex, match.index), sourceOffset + lastIndex, chapterStarts);
        const isHttp = Boolean(match.groups.http);
        const protectedToken = Boolean(match.groups.email || match.groups.unsupported);
        if (protectedToken) mergeText(segments, candidate);
        else {
            const trimmed = trimUrlPunctuation(candidate);
            const url = isHttp ? trimmed : `https://${trimmed}`;
            const link = sourceLink(url);
            if (link) segments.push(link);
            else mergeText(segments, trimmed);
            mergeText(segments, candidate.slice(trimmed.length));
        }
        lastIndex = pattern.lastIndex;
    }
    appendTimestamps(segments, text.slice(lastIndex), sourceOffset + lastIndex, chapterStarts);
};

export const parseEpisodeDescription = (description = '') => {
    const source = String(description || '');
    const links = [];
    let marker = '\ue000';
    while (source.includes(marker)) marker += '\ue000';
    const storeLink = link => `${marker}${links.push(link) - 1}\ue001`;
    let text = flattenMarkup(source, storeLink, decodedSource => {
        while (decodedSource.includes(marker)) marker += '\ue000';
    });
    text = normalizeDescriptionWhitespace(flattenMarkdownLinks(text, storeLink));
    const layout = chapterLayout(text);
    text = layout.text;
    const chapterStarts = layout.meridiemStarts;
    const segments = [];
    const markers = new RegExp(`${marker}(\\d+)\ue001`, 'g');
    let lastIndex = 0, match;
    while ((match = markers.exec(text))) {
        appendPlainText(segments, text.slice(lastIndex, match.index), lastIndex, chapterStarts);
        const segment = links[Number(match[1])];
        if (segment.type === 'text') mergeText(segments, segment.text);
        else segments.push(segment);
        lastIndex = markers.lastIndex;
    }
    appendPlainText(segments, text.slice(lastIndex), lastIndex, chapterStarts);
    segments.forEach((segment, index) => {
        if (segment.type !== 'text' || !/^\n{1,2}$/.test(segment.text) ||
            segments[index - 1]?.type !== 'link' || segments[index + 1]?.type !== 'link') return;
        const before = segments[index - 2], after = segments[index + 2];
        if ((!before || (before.type === 'text' && /\n$/.test(before.text))) &&
            (!after || (after.type === 'text' && /^\n/.test(after.text)))) segment.text = '\n\n';
    });
    return segments;
};

export const descriptionPlaybackRate = rate => Number.isFinite(rate) && rate > 0 ? Math.max(0.1, rate) : 1;

export const descriptionTimestamp = seconds => {
    const value = Number.isFinite(seconds) && seconds >= 0 ? Math.floor(seconds) : 0;
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const ending = `${String(minutes).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
    return hours > 0 ? `${hours}:${ending}` : ending;
};

export const descriptionRemainingTime = seconds => {
    const value = Math.max(0, Math.floor(seconds));
    return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
};

export const currentDescriptionChapter = (segments, currentSeconds, durationSeconds, playbackRate, ownsPlayback) => {
    if (!ownsPlayback || !Number.isFinite(currentSeconds) || currentSeconds < 0) return null;
    const hasDuration = Number.isFinite(durationSeconds) && durationSeconds > 0;
    if (hasDuration && currentSeconds >= durationSeconds) return null;
    const timestamps = [...new Set(segments.filter(segment => segment.type === 'timestamp').map(segment => segment.seconds))].sort((a, b) => a - b);
    const nextIndex = timestamps.findIndex(seconds => seconds > currentSeconds);
    const activeIndex = nextIndex === -1 ? timestamps.length - 1 : nextIndex - 1;
    if (activeIndex < 0) return null;
    const startSeconds = timestamps[activeIndex];
    const nextSeconds = timestamps[activeIndex + 1] ?? Infinity;
    const endSeconds = Math.min(nextSeconds, hasDuration ? durationSeconds : Infinity);
    if (!Number.isFinite(endSeconds) || endSeconds <= currentSeconds) return null;
    const rate = descriptionPlaybackRate(playbackRate);
    return {
        startSeconds,
        endSeconds,
        progress: Math.min(1, Math.max(0, (currentSeconds - startSeconds) / (endSeconds - startSeconds))),
        timeRemaining: descriptionRemainingTime((endSeconds - currentSeconds) / rate),
        maximumRemainingTime: descriptionRemainingTime((endSeconds - startSeconds) / rate),
    };
};
